import { useCompanyLinkInPanel } from "@/hooks/useCompanyLinkInPanel";
import { useTranslation } from "@/hooks/useTranslation";
import { internalLinkTarget } from "@/lib/internal-link-target";
import { cn } from "@/lib/utils";
import { CrmCompaniesToolOutput } from "@shaarp/common";
import { ToolUIPart } from "ai";
import {
  ArrowUpRightIcon,
  Building2Icon,
  CheckCircleIcon,
  ChevronDownIcon,
  Loader2Icon,
  XCircleIcon,
} from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Shimmer } from "../ai-elements/shimmer";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../ui/collapsible";

const CARD_CLASSNAME =
  "overflow-hidden w-full rounded-xl border bg-background not-prose";
const HEADER_CLASSNAME =
  "flex w-full items-center gap-2.5 p-3 text-left text-sm text-muted-foreground";

/**
 * The tool returns either a structured payload or a plain-language string when
 * nothing matched / the CRM holds nothing. The string cases are left to the
 * model's prose, so only the structured shape renders a card.
 */
function isStructuredOutput(
  output: unknown
): output is CrmCompaniesToolOutput {
  return (
    !!output &&
    typeof output === "object" &&
    Array.isArray((output as CrmCompaniesToolOutput).results)
  );
}

/**
 * Renders the companies found in a connected CRM, each linking to its Shaarp
 * record (when resolved) and to the CRM record. The list folds into the header
 * once the lookup settles, so a thread stacking several cards stays readable.
 *
 * One component for every CRM: the card is the same object list whichever one
 * answered, and the provider only decides the icon, the name woven into the
 * labels, and where the outbound link points.
 *
 * @param crmName The CRM's product name, as it reads in the labels.
 * @param icon The CRM's logo, already imported by the caller.
 */
export function ChatToolCrmCompanies({
  crmName,
  icon,
  state,
  output,
}: {
  crmName: string;
  icon: string;
  state: ToolUIPart["state"];
  output: unknown;
}) {
  const { t } = useTranslation();
  const openInPanel = useCompanyLinkInPanel();
  const [open, setOpen] = useState(false);

  // A failed call keeps its state persisted, so falling through to the spinner
  // would leave the card loading forever, including after a reload.
  if (state === "output-error" || state === "output-denied") {
    return (
      <div className={CARD_CLASSNAME}>
        <div className={HEADER_CLASSNAME}>
          <img
            src={icon}
            alt=""
            aria-hidden
            className="object-contain size-4 shrink-0"
          />
          <span className="font-medium text-foreground">
            {t("chat.message.tool.crmCompanies.error", { crm: crmName })}
          </span>
          <XCircleIcon className="size-3.5 shrink-0 text-red-600" />
        </div>
      </div>
    );
  }

  if (state !== "output-available") {
    return (
      <div className={CARD_CLASSNAME}>
        <div className={HEADER_CLASSNAME}>
          <img
            src={icon}
            alt=""
            aria-hidden
            className="object-contain size-4 shrink-0"
          />
          <Shimmer className="font-medium">
            {t("chat.message.tool.crmCompanies.loading", { crm: crmName })}
          </Shimmer>
          <Loader2Icon className="size-3.5 shrink-0 animate-spin" />
        </div>
      </div>
    );
  }

  if (!isStructuredOutput(output) || output.results.length === 0) {
    return null;
  }

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className={cn("group/tool", CARD_CLASSNAME)}
    >
      <CollapsibleTrigger
        className={cn(HEADER_CLASSNAME, "transition-colors hover:text-foreground")}
      >
        <img
          src={icon}
          alt=""
          aria-hidden
          className="object-contain size-4 shrink-0"
        />
        <span className="font-medium text-foreground">
          {t("chat.message.tool.crmCompanies.title", {
            count: output.returned,
            crm: crmName,
          })}
        </span>
        <CheckCircleIcon className="size-3.5 shrink-0 text-green-600" />
        <ChevronDownIcon className="ml-auto size-4 shrink-0 transition-transform group-data-[state=open]/tool:rotate-180" />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <ul className="border-t divide-y">
          {output.results.map((company, index) => {
            // Threads written before the card was made generic persisted the
            // link as `hubspotUrl`; reading both is what keeps their rows
            // clickable after a reload.
            const crmUrl = company.crmUrl ?? company.hubspotUrl ?? null;

            return (
              <li
                key={`${company.domain ?? company.name ?? "company"}-${index}`}
                className="flex gap-3 items-center px-3 py-2.5"
              >
                <Building2Icon className="shrink-0 size-4 text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">
                    {company.name ?? company.domain ?? "—"}
                  </p>
                  <p className="text-xs truncate text-muted-foreground">
                    {[
                      company.domain,
                      company.sector,
                      company.city ?? company.region,
                      company.headcount,
                    ]
                      .filter(Boolean)
                      .join(" · ") ||
                      t("chat.message.tool.crmCompanies.noDetails")}
                  </p>
                </div>
                <div className="flex gap-1 items-center shrink-0">
                  {company.shaarpCompanyId && (
                    <Link
                      to={`/company/${company.shaarpCompanyId}`}
                      target={internalLinkTarget()}
                      rel="noreferrer"
                      onClick={(event) =>
                        company.shaarpCompanyId &&
                        openInPanel(event, {
                          id: company.shaarpCompanyId,
                          label: company.name ?? "",
                        })
                      }
                      className={cn(
                        "inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs",
                        "transition-colors hover:bg-muted"
                      )}
                    >
                      {t("chat.message.tool.crmCompanies.openInShaarp")}
                    </Link>
                  )}
                  {crmUrl && (
                    <a
                      href={crmUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex gap-1 items-center px-2 py-1 text-xs rounded-md border transition-colors hover:bg-muted"
                    >
                      {crmName}
                      <ArrowUpRightIcon className="size-3" />
                    </a>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </CollapsibleContent>
    </Collapsible>
  );
}
